import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faFilter,
  faSliders,
  faChevronDown,
  faChevronRight,
  faPlus,
  faTag,
  faUserGroup,
  faToggleOn,
  faToggleOff,
  faGripVertical,
  faRightLeft,
  faBan,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import {
  fetchSkillsLibrary,
  type LibrarySkill,
  type LibraryAlias,
  type Origin,
} from '../api/skillsLibrary';
import { repointAlias, setAliasActive, setNodeActive } from '../api/skillsTaxonomy';

// Shared column grid (skills_library_spec.md §9): Skill | Labels | Origin | Assigned | actions.
const GRID = 'minmax(0,1fr) 190px 108px 84px 36px';

type Toast = { kind: 'info' | 'error'; text: string };

/** Uppercased first char of the label, or '#'. Mirrors the backend grouping. */
function groupLetter(label: string): string {
  const t = label.trim();
  return t ? t[0].toUpperCase() : '#';
}

function OriginBadge({ origin }: { origin: Origin }) {
  const isCv = origin === 'CV';
  return (
    <span
      className="inline-block rounded-full px-2 py-0.5 text-[11px] font-medium"
      style={
        isCv
          ? { background: '#eff6ff', color: '#1d4ed8' }
          : { background: '#faf5ff', color: '#7e22ce' }
      }
    >
      {isCv ? 'CV' : 'Tender'}
    </span>
  );
}

export default function SkillsLibrary() {
  const { t } = useTranslation();
  const [skills, setSkills] = useState<LibrarySkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editingAliasId, setEditingAliasId] = useState<string | null>(null);
  const [dragAliasId, setDragAliasId] = useState<string | null>(null);
  const [dragOverSkillId, setDragOverSkillId] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  const flash = useCallback((next: Toast) => {
    setToast(next);
    window.setTimeout(() => setToast((cur) => (cur === next ? null : cur)), 4000);
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await fetchSkillsLibrary();
      setSkills(data.groups.flatMap((g) => g.skills));
      setLoadError(null);
    } catch {
      setLoadError(t('skillsLibrary.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const activeSkills = useMemo(() => skills.filter((s) => s.status === 'ACTIVE'), [skills]);

  // Filter (client-side, D8) across skill name, description, and any alias text,
  // then group by first letter (A→Z) with skills name-ordered (§3.1).
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (s: LibrarySkill) =>
      !q ||
      s.name.toLowerCase().includes(q) ||
      (s.description ?? '').toLowerCase().includes(q) ||
      s.aliases.some((a) => a.text.toLowerCase().includes(q));

    const byLetter = new Map<string, LibrarySkill[]>();
    for (const s of skills.filter(matches)) {
      const letter = groupLetter(s.name);
      const bucket = byLetter.get(letter) ?? [];
      bucket.push(s);
      byLetter.set(letter, bucket);
    }
    return [...byLetter.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([letter, list]) => ({
        letter,
        skills: [...list].sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }, [skills, search]);

  const toggleExpanded = (nodeId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });

  // --- mutations (optimistic, revert-on-reject — §8) -------------------------

  const errText = (e: unknown, fallback: string): string => {
    const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
    return msg || fallback;
  };

  const handleToggleNode = async (skill: LibrarySkill) => {
    const activate = skill.status !== 'ACTIVE';
    const prev = skill.status;
    setSkills((cur) =>
      cur.map((s) => (s.id === skill.id ? { ...s, status: activate ? 'ACTIVE' : 'DEPRECATED' } : s)),
    );
    // §3.6: deactivating a node with active aliases leaves those aliases active
    // (surfaced as lint in the curation queue) — inform the curator.
    if (!activate && skill.aliases.some((a) => a.status === 'ACTIVE')) {
      flash({ kind: 'info', text: t('skillsLibrary.deactivateActiveAliasesNotice') });
    }
    try {
      await setNodeActive(skill.id, activate);
    } catch (e) {
      setSkills((cur) => cur.map((s) => (s.id === skill.id ? { ...s, status: prev } : s)));
      flash({ kind: 'error', text: errText(e, t('skillsLibrary.toggleNodeError')) });
    }
  };

  const handleToggleAlias = async (skill: LibrarySkill, alias: LibraryAlias) => {
    const activate = alias.status !== 'ACTIVE';
    const prev = alias.status;
    const patch = (status: LibraryAlias['status']) =>
      setSkills((cur) =>
        cur.map((s) =>
          s.id === skill.id
            ? { ...s, aliases: s.aliases.map((a) => (a.id === alias.id ? { ...a, status } : a)) }
            : s,
        ),
      );
    patch(activate ? 'ACTIVE' : 'DEPRECATED');
    try {
      await setAliasActive(alias.id, activate);
    } catch (e) {
      patch(prev);
      flash({ kind: 'error', text: errText(e, t('skillsLibrary.toggleAliasError')) });
    }
  };

  const doRepoint = async (alias: LibraryAlias, fromSkillId: string, targetId: string) => {
    if (targetId === fromSkillId) return; // no-op
    const target = skills.find((s) => s.id === targetId);
    if (!target) return;
    // Client-side pre-checks (§8) — reserve snap-back for genuine races.
    if (target.status !== 'ACTIVE') {
      flash({ kind: 'error', text: t('skillsLibrary.repointInactiveError') });
      return;
    }
    if (target.aliases.some((a) => a.status === 'ACTIVE' && a.text.toLowerCase() === alias.text.toLowerCase())) {
      flash({
        kind: 'error',
        text: t('skillsLibrary.repointDuplicateError', { text: alias.text, target: target.name }),
      });
      return;
    }

    const snapshot = skills;
    // Optimistically move the alias to its new home and auto-expand the target (§4.5).
    setSkills((cur) =>
      cur.map((s) => {
        if (s.id === fromSkillId) return { ...s, aliases: s.aliases.filter((a) => a.id !== alias.id) };
        if (s.id === targetId) return { ...s, aliases: [...s.aliases, alias] };
        return s;
      }),
    );
    setExpanded((prev) => new Set(prev).add(targetId));
    setEditingAliasId(null);
    try {
      await repointAlias(alias.id, targetId);
      // Assigned/Origins are server-derived; refetch quietly to reconcile counts.
      void load();
    } catch (e) {
      setSkills(snapshot);
      flash({ kind: 'error', text: errText(e, t('skillsLibrary.repointError')) });
    }
  };

  // --- drag & drop (native HTML5) --------------------------------------------

  const onDropToSkill = (targetId: string) => {
    const aliasId = dragAliasId;
    clearDrag();
    if (!aliasId) return;
    const from = skills.find((s) => s.aliases.some((a) => a.id === aliasId));
    const alias = from?.aliases.find((a) => a.id === aliasId);
    if (from && alias) void doRepoint(alias, from.id, targetId);
  };

  const clearDrag = () => {
    setDragAliasId(null);
    setDragOverSkillId(null);
  };

  // --- render ----------------------------------------------------------------

  if (loading) {
    return (
      <div className="mx-auto max-w-[1180px] px-4 py-10 text-center text-slate-500">
        {t('skillsLibrary.loading')}
      </div>
    );
  }

  return (
    <div style={{ background: '#f1f5f9' }} className="min-h-full">
      <div className="mx-auto max-w-[1180px] px-4 py-6" style={{ color: '#0f172a' }}>
        <header className="mb-4">
          <h1 className="text-2xl font-bold">{t('skillsLibrary.title')}</h1>
          <p className="mt-1 text-sm" style={{ color: '#64748b' }}>
            {t('skillsLibrary.subtitle')}
          </p>
        </header>

        <div className="rounded-[14px] border bg-white shadow-sm" style={{ borderColor: '#e2e8f0' }}>
          {/* Toolbar */}
          <div className="flex items-center gap-3 border-b p-3" style={{ borderColor: '#e2e8f0' }}>
            <div className="relative w-full max-w-[340px]">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('skillsLibrary.searchPlaceholder')}
                className="w-full rounded-lg border py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-blue-200"
                style={{ borderColor: '#e2e8f0' }}
              />
            </div>
            <div className="flex-1" />
            <button
              type="button"
              disabled
              title={t('skillsLibrary.filterTitle')}
              className="rounded-lg border px-3 py-2 text-sm text-slate-500 disabled:opacity-50"
              style={{ borderColor: '#e2e8f0' }}
            >
              <FontAwesomeIcon icon={faFilter} className="mr-1.5" />
              {t('skillsLibrary.filter')}
            </button>
            <button
              type="button"
              disabled
              title={t('skillsLibrary.displayTitle')}
              className="rounded-lg border px-3 py-2 text-sm text-slate-500 disabled:opacity-50"
              style={{ borderColor: '#e2e8f0' }}
            >
              <FontAwesomeIcon icon={faSliders} className="mr-1.5" />
              {t('skillsLibrary.display')}
            </button>
          </div>

          {/* Column header */}
          <div
            className="grid items-center gap-3 border-b px-4 py-2 text-[11px] font-semibold uppercase"
            style={{ gridTemplateColumns: GRID, borderColor: '#e2e8f0', color: '#94a3b8' }}
          >
            <div>{t('skillsLibrary.colSkill')}</div>
            <div>{t('skillsLibrary.colLabels')}</div>
            <div>{t('skillsLibrary.colOrigin')}</div>
            <div className="text-right">{t('skillsLibrary.colAssigned')}</div>
            <div />
          </div>

          {loadError && <div className="p-4 text-sm text-red-600">{loadError}</div>}

          {!loadError && groups.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500">{t('skillsLibrary.noResults')}</div>
          )}

          {groups.map((group) => (
            <div key={group.letter}>
              <div
                className="flex items-center gap-2 bg-slate-50 px-4 py-1.5 text-xs font-semibold text-slate-500"
              >
                <FontAwesomeIcon icon={faChevronDown} className="text-slate-300" />
                <span>{group.letter}</span>
                <span className="text-slate-400">({group.skills.length})</span>
                <div className="flex-1" />
                <FontAwesomeIcon icon={faPlus} className="text-slate-300" title={t('skillsLibrary.addSkillTitle')} />
              </div>

              {group.skills.map((skill) => (
                <SkillRow
                  key={skill.id}
                  skill={skill}
                  activeSkills={activeSkills}
                  expanded={expanded.has(skill.id)}
                  onToggleExpand={() => toggleExpanded(skill.id)}
                  onToggleNode={() => handleToggleNode(skill)}
                  onToggleAlias={(alias) => handleToggleAlias(skill, alias)}
                  editingAliasId={editingAliasId}
                  setEditingAliasId={setEditingAliasId}
                  onRepointSelect={(alias, targetId) => doRepoint(alias, skill.id, targetId)}
                  dragAliasId={dragAliasId}
                  setDragAliasId={setDragAliasId}
                  dragOverSkillId={dragOverSkillId}
                  setDragOverSkillId={setDragOverSkillId}
                  onDropToSkill={onDropToSkill}
                  clearDrag={clearDrag}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {toast && (
        <div
          className={`fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg px-4 py-2 text-sm text-white shadow-lg ${
            toast.kind === 'error' ? 'bg-red-600' : 'bg-slate-800'
          }`}
          role="status"
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skill row + its expandable alias panel
// ---------------------------------------------------------------------------

interface SkillRowProps {
  skill: LibrarySkill;
  activeSkills: LibrarySkill[];
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleNode: () => void;
  onToggleAlias: (alias: LibraryAlias) => void;
  editingAliasId: string | null;
  setEditingAliasId: (id: string | null) => void;
  onRepointSelect: (alias: LibraryAlias, targetId: string) => void;
  dragAliasId: string | null;
  setDragAliasId: (id: string | null) => void;
  dragOverSkillId: string | null;
  setDragOverSkillId: (id: string | null) => void;
  onDropToSkill: (targetId: string) => void;
  clearDrag: () => void;
}

function SkillRow({
  skill,
  activeSkills,
  expanded,
  onToggleExpand,
  onToggleNode,
  onToggleAlias,
  editingAliasId,
  setEditingAliasId,
  onRepointSelect,
  dragAliasId,
  setDragAliasId,
  dragOverSkillId,
  setDragOverSkillId,
  onDropToSkill,
  clearDrag,
}: SkillRowProps) {
  const { t } = useTranslation();
  const inactive = skill.status !== 'ACTIVE';
  const isDropTarget = !inactive && dragOverSkillId === skill.id;

  // Label truncation (§3.2): ≥3 labels → first + "+N"; else show all.
  const labels = skill.labels;
  const shownLabels = labels.length >= 3 ? labels.slice(0, 1) : labels;
  const overflow = labels.length >= 3 ? labels.length - 1 : 0;

  const allowDrop = !inactive && dragAliasId != null;

  return (
    <div
      onDragOver={(e) => {
        if (!allowDrop) return;
        e.preventDefault();
        setDragOverSkillId(skill.id);
      }}
      onDragLeave={() => dragOverSkillId === skill.id && setDragOverSkillId(null)}
      onDrop={(e) => {
        if (!allowDrop) return;
        e.preventDefault();
        onDropToSkill(skill.id);
      }}
      style={{ outline: isDropTarget ? '2px solid #2563eb' : '2px solid transparent' }}
    >
      {/* Summary row */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        className="grid cursor-pointer items-center gap-3 border-b px-4 py-2.5 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
        style={{ gridTemplateColumns: GRID, borderColor: '#f1f5f9' }}
        onClick={onToggleExpand}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleExpand();
          }
        }}
      >
        <div className="flex min-w-0 items-center gap-2">
          <FontAwesomeIcon
            icon={expanded ? faChevronDown : faChevronRight}
            className="text-slate-400"
          />
          <div className="min-w-0">
            <div
              className="truncate text-sm font-medium"
              style={{ color: inactive ? '#94a3b8' : '#0f172a' }}
            >
              {skill.name}
              {inactive && (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                  {t('skillsLibrary.inactive')}
                </span>
              )}
            </div>
            <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-400">
              <FontAwesomeIcon icon={faTag} />
              {t('skillsLibrary.aliasCount', { count: skill.aliases.length })}
            </div>
          </div>
        </div>

        {/* Labels */}
        <div className="flex flex-wrap items-center gap-1">
          {shownLabels.map((label) => (
            <span
              key={label}
              className="rounded-full border px-2 py-0.5 text-[11px]"
              style={{ borderColor: '#e2e8f0', background: '#fff' }}
            >
              {label}
            </span>
          ))}
          {overflow > 0 && (
            <span className="rounded-full px-2 py-0.5 text-[11px] text-slate-500" style={{ background: '#f1f5f9' }}>
              +{overflow}
            </span>
          )}
        </div>

        {/* Origin */}
        <div className="flex flex-wrap items-center gap-1">
          {skill.origins.map((o) => (
            <OriginBadge key={o} origin={o} />
          ))}
        </div>

        {/* Assigned */}
        <div className="flex items-center justify-end gap-1.5 text-sm text-slate-600">
          <FontAwesomeIcon icon={faUserGroup} className="text-slate-400" />
          {skill.assigned}
        </div>

        {/* Node toggle */}
        <div className="flex justify-end">
          <button
            type="button"
            title={inactive ? t('skillsLibrary.activateSkill') : t('skillsLibrary.deactivateSkill')}
            onClick={(e) => {
              e.stopPropagation();
              onToggleNode();
            }}
            className="text-lg"
            style={{ color: inactive ? '#94a3b8' : '#16a34a' }}
          >
            <FontAwesomeIcon icon={inactive ? faToggleOff : faToggleOn} />
          </button>
        </div>
      </div>

      {/* Expanded alias panel */}
      {expanded && (
        <div className="border-b bg-slate-50/60 px-4 py-3" style={{ borderColor: '#f1f5f9' }}>
          <div className="mb-2 text-[11px] font-semibold uppercase text-slate-400">
            {t('skillsLibrary.aliasesHeader')}
          </div>
          {skill.aliases.length === 0 ? (
            <div className="rounded-lg border border-dashed p-4 text-center text-sm text-slate-400" style={{ borderColor: '#cbd5e1' }}>
              {t('skillsLibrary.noAliases')}
            </div>
          ) : (
            <div className="space-y-1">
              {[...skill.aliases]
                .sort((a, b) => a.text.localeCompare(b.text))
                .map((alias) => (
                  <AliasRow
                    key={alias.id}
                    alias={alias}
                    skillId={skill.id}
                    activeSkills={activeSkills}
                    editing={editingAliasId === alias.id}
                    setEditing={(on) => setEditingAliasId(on ? alias.id : null)}
                    onRepointSelect={(targetId) => onRepointSelect(alias, targetId)}
                    onToggleAlias={() => onToggleAlias(alias)}
                    dragging={dragAliasId === alias.id}
                    setDragAliasId={setDragAliasId}
                    clearDrag={clearDrag}
                  />
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One alias row
// ---------------------------------------------------------------------------

interface AliasRowProps {
  alias: LibraryAlias;
  skillId: string;
  activeSkills: LibrarySkill[];
  editing: boolean;
  setEditing: (on: boolean) => void;
  onRepointSelect: (targetId: string) => void;
  onToggleAlias: () => void;
  dragging: boolean;
  setDragAliasId: (id: string | null) => void;
  clearDrag: () => void;
}

function AliasRow({
  alias,
  skillId,
  activeSkills,
  editing,
  setEditing,
  onRepointSelect,
  onToggleAlias,
  dragging,
  setDragAliasId,
  clearDrag,
}: AliasRowProps) {
  const { t } = useTranslation();
  const deprecated = alias.status !== 'ACTIVE';

  return (
    <div style={{ opacity: dragging ? 0.4 : 1 }}>
      <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-2" style={{ border: '1px solid #f1f5f9' }}>
        <button
          type="button"
          draggable
          onDragStart={(e) => {
            setDragAliasId(alias.id);
            e.dataTransfer.setData('text/plain', alias.id);
            e.dataTransfer.effectAllowed = 'move';
          }}
          onDragEnd={clearDrag}
          title={t('skillsLibrary.dragToRepoint')}
          className="cursor-grab text-slate-300 hover:text-slate-500"
        >
          <FontAwesomeIcon icon={faGripVertical} />
        </button>

        <code
          className="font-mono text-sm"
          style={deprecated ? { textDecoration: 'line-through', color: '#94a3b8' } : undefined}
        >
          {alias.text}
        </code>

        <div className="flex-1" />

        {/* Status */}
        {deprecated ? (
          <span className="rounded-full px-2 py-0.5 text-[11px]" style={{ background: '#f1f5f9', color: '#64748b' }}>
            {t('skillsLibrary.deprecated')}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: '#16a34a' }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: '#22c55e' }} />
            {t('skillsLibrary.active')}
          </span>
        )}

        {/* Origin */}
        <div className="flex w-[108px] items-center gap-1">
          {alias.origins.map((o) => (
            <OriginBadge key={o} origin={o} />
          ))}
        </div>

        {/* Repoint */}
        <button
          type="button"
          title={t('skillsLibrary.repoint')}
          onClick={() => setEditing(!editing)}
          className="text-slate-400 hover:text-blue-600"
        >
          <FontAwesomeIcon icon={faRightLeft} />
        </button>

        {/* Deprecate / reactivate */}
        <button
          type="button"
          title={deprecated ? t('skillsLibrary.reactivateAlias') : t('skillsLibrary.deprecateAlias')}
          onClick={onToggleAlias}
          style={{ color: deprecated ? '#16a34a' : '#dc2626' }}
          className="hover:opacity-80"
        >
          <FontAwesomeIcon icon={deprecated ? faRotateLeft : faBan} />
        </button>
      </div>

      {/* Inline repoint editor */}
      {editing && (
        <div className="mt-1 flex items-center gap-2 px-3 py-1.5 text-sm">
          <span className="text-slate-500">{t('skillsLibrary.repointTo')}</span>
          <select
            className="min-w-[220px] rounded-md border px-2 py-1 text-sm"
            style={{ borderColor: '#e2e8f0' }}
            defaultValue=""
            onChange={(e) => {
              const targetId = e.target.value;
              if (targetId) onRepointSelect(targetId);
            }}
          >
            <option value="">{t('skillsLibrary.chooseSkill')}</option>
            {activeSkills
              .filter((s) => s.id !== skillId)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </select>
        </div>
      )}
    </div>
  );
}
